import { DateHelper, StringHelper } from '@bryntum/gantt';
import { useCallback, useEffect, useMemo } from 'react';
import { UserType } from 'api/models';
import { useUser } from 'hooks/use-user';
import { useProjectBaselineQuery } from 'queries/project-baseline-mutation';
import { getDaysDiff } from 'utils/date-shift';
import {
  BASELINE_SELECTION_MAX_COUNT,
  useBaselineSelection,
} from '../baseline';

export const useBaselineConfig = ({
  ganttRef,
  projectId,
}: {
  ganttRef: any;
  projectId: number;
}) => {
  const { data: baselines } = useProjectBaselineQuery(projectId);
  const { user } = useUser();
  const isExternalUser = user.userType === UserType.External;

  const toggleInGantt = useCallback(
    (index: number, value: boolean) => {
      ganttRef?.ganttInstance.element.classList[value ? 'add' : 'remove'](
        `b-show-baseline-${index + 1}`,
      );
    },
    [ganttRef?.ganttInstance.element.classList],
  );

  const { selection, initialIndices, set, unset } = useBaselineSelection({
    projectId,
  });

  useEffect(() => {
    ganttRef?.ganttInstance.element.classList[
      selection.length >= BASELINE_SELECTION_MAX_COUNT ? 'add' : 'remove'
    ]('b-max-baseline-selection');
  }, [ganttRef?.ganttInstance.element.classList, selection]);

  const toggleBaseline = useCallback(
    (id: number, index: number, value: boolean) => {
      if (value) {
        set(id);
      } else {
        unset(id);
      }
      toggleInGantt(index, value);
    },
    [set, toggleInGantt, unset],
  );

  const setBaseline = useCallback(
    (index?: number) => {
      const _index = index ?? (baselines?.length ?? 0) + 1;

      ganttRef?.ganttInstance.taskStore.setBaseline(_index);
    },
    [baselines?.length, ganttRef],
  );

  useEffect(() => {
    if (!ganttRef?.ganttInstance?.element) {
      return;
    }
    initialIndices.forEach((index) => toggleInGantt(index, true));
  }, [ganttRef?.ganttInstance?.element, initialIndices, toggleInGantt]);

  useEffect(() => {
    if (!ganttRef) {
      return;
    }

    ganttRef?.instance?.features.baselines.setConfig({
      template(data) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const me: any = this;
        const { baseline } = data;
        const { task } = baseline;
        const delayed = task.startDate > baseline.startDate;
        const overrun = task.durationMS > baseline.durationMS;
        const index = task.baselines.indexOf(baseline);

        let { decimalPrecision } = me;

        if (decimalPrecision == null) {
          decimalPrecision = me.client.durationDisplayPrecision;
        }

        // eslint-disable-next-line no-restricted-properties
        const multiplier = Math.pow(10, decimalPrecision);
        const displayDuration =
          Math.round(baseline.duration * multiplier) / multiplier;

        return `
      <div class="b-gantt-baseline-tooltip-wrapper">
    <div class="b-gantt-task-title">${StringHelper.encodeHtml(task.name)}</div>
    <div class="baseline-name-wrapper">(<div class="baseline-name">${
      baselines?.[index]?.baselineName || ''
    }</div>)</div>
    <ul class="baseline-data-table">
    <li><div>${me.L('Start')}:</div><div>${data.startClockHtml}</div></li>
    ${
      baseline.milestone
        ? ''
        : `
        <li><div>${me.L('End')}:</div><div>${data.endClockHtml}</div></li>
        <li><div>${me.L(
          'Duration',
        )}:</div><div class="b-right">${`${displayDuration} ${DateHelper.getLocalizedNameOfUnit(
            baseline.durationUnit,
            baseline.duration !== 1,
          )}`}</div></li>
    `
    }
    </ul>
    ${
      delayed
        ? `
        <div class="statusmessage b-baseline-delay baseline-status-message"><i class="statusicon b-fa b-fa-exclamation-triangle"></i>${me.L(
          'Delayed start by',
        )} ${getDaysDiff(task.startDate, baseline.startDate)} day(s)</div>
    `
        : ''
    }
    ${
      overrun
        ? `
        <div class="statusmessage b-baseline-overrun baseline-status-message"><i class="statusicon b-fa b-fa-exclamation-triangle"></i>${me.L(
          'Overrun by',
        )} ${DateHelper.formatDelta(
            task.durationMS - baseline.durationMS,
          )}</div>
    `
        : ''
    }
    </div>
    `;
      },
    });
  }, [baselines, ganttRef]);

  const config = useMemo(() => {
    return {
      baselinesFeature: {
        disabled: isExternalUser,
      },
    };
  }, [isExternalUser]);

  return {
    baselines,
    selection,
    setBaseline,
    toggleBaseline,
    config,
  };
};
